import { Icon } from '@virtidev/toolbox';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AnimateHeight from 'react-animate-height';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentHintSelector,
  hitObjectiveIdsSelector,
  markShownObjectiveHint,
  setCurrentHint,
  shownHintObjectiveIdsSelector,
  unsetCurrentHint,
  groupIdHintIndicatorShownSelector,
  hintScoreWarningShownSelector,
  setHintScoreWarningShown,
  setObjectiveGroupComplete,
} from '../../../slices/ObjectivesSlice';
import * as Styled from './ObjectiveGroup.styled';
import Objective from './Objective/Objective';
import { useShouldShowHints } from '../../vh-types/vhTypeHooks';
import ObjectiveProgressCircle from '../../ObjectiveProgressCircle/ObjectiveProgressCircle';
import DismissablePopover from '../../DismissablePopover/DismissablePopover';
import { usePrevious } from '../../../../../../utility/CustomHooks';
import { ChatStates, selectChatState } from '../../../slices/chat/ChatSlice';
import { easyModeSelector } from '../../../slices/VirtualHumanSlice';
import useObjectiveGroupFractionComplete from '../../ObjectivesLogicHandler/ObjectiveGroupLogicHandler/useObjectiveGroupFractionComplete';
import { useTranslation } from 'react-i18next';

const ObjectiveGroup = ({ objectiveGroup }) => {
  const dispatch = useDispatch();
  const shouldShowHints = useShouldShowHints();
  const currentHint = useSelector(currentHintSelector);

  const objectivesWithPoints = useMemo(() => {
    return objectiveGroup.objectives.filter((objective) => {
      return (
        objective.scoreMetadata?.points && objective.scoreMetadata.points > 0
      );
    });
  }, [objectiveGroup]);

  const shownHintObjectiveIds = useSelector(shownHintObjectiveIdsSelector);
  const hitObjectiveIds = useSelector(hitObjectiveIdsSelector);

  const objectivesWithHintsNotBeenHit = useMemo(() => {
    return objectivesWithPoints.filter(
      (objective) => !hitObjectiveIds.includes(objective.id)
    );
  }, [objectivesWithPoints, hitObjectiveIds]);

  const remainingObjectivesToBeHinted = useMemo(() => {
    return objectivesWithPoints.filter((objective) => {
      return (
        !shownHintObjectiveIds.includes(objective.id) &&
        !hitObjectiveIds.includes(objective.id)
      );
    });
  }, [objectivesWithPoints, shownHintObjectiveIds, hitObjectiveIds]);

  const [rehintLoopingHintIndex, setRehintLoopingHintIndex] = useState(0);

  const hintScoreWarningShown = useSelector(hintScoreWarningShownSelector);
  const [showHintScoreWarningPopover, setShowHintScoreWarningPopover] =
    useState(false);

  const easyMode = useSelector(easyModeSelector);
  const handleHint = useCallback(() => {
    // show score warning if first click (and not easy mode)
    if (!hintScoreWarningShown && !easyMode) {
      dispatch(setHintScoreWarningShown());
      setShowHintScoreWarningPopover(true);
      return;
    }
    setExpanded(true);

    setShowHintScoreWarningPopover(false);
    setShowUseHintPopover(false);
    if (remainingObjectivesToBeHinted.length > 0) {
      // we only need to mark in the log that there's been a first hint if easy mode is off
      if (!easyMode) {
        dispatch(markShownObjectiveHint(remainingObjectivesToBeHinted[0].id));
      }
      dispatch(
        setCurrentHint({
          objectiveId: remainingObjectivesToBeHinted[0].id,
          text: remainingObjectivesToBeHinted[0].hint,
        })
      );
    } else if (objectivesWithHintsNotBeenHit.length > 0) {
      // when we've shown all hints, start looping through the ones that have been shown already
      dispatch(
        setCurrentHint({
          objectiveId: objectivesWithHintsNotBeenHit[rehintLoopingHintIndex].id,
          text: objectivesWithHintsNotBeenHit[rehintLoopingHintIndex].hint,
        })
      );
      const newLoopingIndex =
        objectivesWithHintsNotBeenHit[rehintLoopingHintIndex + 1] !== undefined
          ? rehintLoopingHintIndex + 1
          : 0;
      setRehintLoopingHintIndex(newLoopingIndex);
    }
  }, [
    dispatch,
    remainingObjectivesToBeHinted,
    rehintLoopingHintIndex,
    objectivesWithHintsNotBeenHit,
    hintScoreWarningShown,
    easyMode,
  ]);

  // unset active hint if we hit its associated objective
  useEffect(() => {
    if (
      currentHint?.objectiveId &&
      hitObjectiveIds.includes(currentHint.objectiveId)
    ) {
      dispatch(unsetCurrentHint());
    }
  }, [currentHint, dispatch, hitObjectiveIds]);

  const [expanded, setExpanded] = useState(easyMode ? true : false);
  const handleClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const fractionComplete = useObjectiveGroupFractionComplete(objectiveGroup);

  const [showUseHintPopover, setShowUseHintPopover] = useState(false);
  const attachedPopoverRef = useRef();
  const groupIdHintIndicatorBeenShown = useSelector(
    groupIdHintIndicatorShownSelector
  );
  const prevGroupIdHintIndicatorBeenShown = usePrevious(
    groupIdHintIndicatorBeenShown
  );
  useEffect(() => {
    if (
      !prevGroupIdHintIndicatorBeenShown &&
      groupIdHintIndicatorBeenShown === objectiveGroup.id
    ) {
      setShowUseHintPopover(true);
    }
  }, [
    groupIdHintIndicatorBeenShown,
    objectiveGroup.id,
    prevGroupIdHintIndicatorBeenShown,
  ]);

  const setInactiveHintIndicatorPopover = useCallback(() => {
    setShowUseHintPopover(false);
  }, []);
  const setInactiveScoreWarningPopover = useCallback(() => {
    setShowHintScoreWarningPopover(false);
  }, []);

  const chatState = useSelector(selectChatState);
  const hintsDisabled =
    chatState !== ChatStates.TALK || objectivesWithHintsNotBeenHit.length === 0;

  const { t } = useTranslation();

  return (
    <Styled.ObjectiveGroupWrapper key={objectiveGroup.id}>
      <Styled.ObjectiveGroup>
        <Styled.ObjectiveGroupName
          onClick={handleClick}
          expanded={expanded}
          shouldShowHints={shouldShowHints}
          complete={fractionComplete >= 1}
        >
          <ObjectiveProgressCircle
            pathColor="var(--primary-color)"
            percentageFraction={fractionComplete}
          />
          <span>{objectiveGroup.name}</span>
          <Icon icon="chevron" />
        </Styled.ObjectiveGroupName>
        {shouldShowHints && (
          <>
            <Styled.HintButton
              ref={attachedPopoverRef}
              disabled={hintsDisabled}
              onClick={handleHint}
            >
              <Styled.HintIcon icon="hint" />
            </Styled.HintButton>
            <DismissablePopover
              attachToRef={attachedPopoverRef}
              position={'bottom-right'}
              active={showUseHintPopover}
              setInactive={setInactiveHintIndicatorPopover}
            >
              {t('clickForHint')}
            </DismissablePopover>
            <DismissablePopover
              attachToRef={attachedPopoverRef}
              position={'bottom-right'}
              active={showHintScoreWarningPopover}
              setInactive={setInactiveScoreWarningPopover}
            >
              {t('hintsMeansNoPointsWarning')}
            </DismissablePopover>
          </>
        )}
      </Styled.ObjectiveGroup>

      <AnimateHeight height={expanded ? 'auto' : 0}>
        <Styled.ObjectivesListWrapper>
          {objectivesWithPoints.map((objective) => (
            <Objective key={objective.id} objective={objective} />
          ))}
        </Styled.ObjectivesListWrapper>
      </AnimateHeight>
    </Styled.ObjectiveGroupWrapper>
  );
};

export default ObjectiveGroup;
